// Procedural voxel warships — chunky boxes only, no asset files, same
// aesthetic as the planes. Hulls are built bow-along-+X / up-+Y /
// starboard-+Z and sit with the waterline at y=0, so the server's anchor
// position and yaw apply directly. Dimensions mirror internal/game/ships.go
// (the carrier's six twin AA mounts ride the deck edges at x ±38/0, z ±13).

import * as THREE from "three";
import { mergeBoxes, TEAM_INFO } from "./planes.js";

const SHIP_CARRIER = 0;
const SHIP_BATTLESHIP = 2;

const hullGray = 0x5b6068;
const hullDark = 0x474b52;
const deckDark = 0x3a3d44;
const deckLine = 0xd8d2bc;
const superstructure = 0x6d727b;
const gunmetal = 0x1c1e22;
const glass = 0x9fd8f0;
const funnelDark = 0x2b2f36;
const navRed = 0xff4444;
const navGreen = 0x35e06a;

/** Darken/lighten a hex color by factor k. */
function shade(hex, k) {
  return new THREE.Color(hex).multiplyScalar(k).getHex();
}

/** Duplicate boxes across the z=0 plane (port/starboard symmetry). */
const mirrorZ = (boxes) => boxes.flatMap((b) => [b, { ...b, z: -b.z }]);

// Fleet carrier: ~124 m flight deck on a slab hull, island to starboard,
// painted centerline and team-colored deck edges. Deck top sits at y=15,
// matching the server's gun pivots two meters above it.
function carrierBoxes(t) {
  const boxes = [
    // Hull: deep slab with a darker boot line at the waterline, plus
    // tapered bow and stern blocks.
    { x: 0, y: 3.5, z: 0, w: 104, h: 13, d: 24, color: hullGray },
    { x: 0, y: -1.5, z: 0, w: 104, h: 5, d: 24.4, color: hullDark },
    { x: 56, y: 4.5, z: 0, w: 14, h: 11, d: 17, color: hullGray },
    { x: 65, y: 6.5, z: 0, w: 6, h: 7, d: 10, color: hullGray },
    { x: -56, y: 4.5, z: 0, w: 10, h: 11, d: 19, color: hullGray },
    // Hangar band with the team's recognition stripe.
    { x: 0, y: 11.2, z: 0, w: 96, h: 2.4, d: 25, color: shade(t.body, 0.85) },
    // Flight deck: full-length dark slab, slightly proud of the hull.
    { x: 1, y: 14.2, z: 0, w: 124, h: 1.6, d: 27, color: deckDark },
    // Painted centerline dashes down the deck.
    ...[-48, -32, -16, 0, 16, 32, 48].map((x) => ({
      x, y: 15.06, z: 0, w: 9, h: 0.12, d: 1.1, color: deckLine,
    })),
    // Bow/stern threshold stripes and team-colored deck edges.
    { x: 59, y: 15.06, z: 0, w: 3, h: 0.12, d: 22, color: t.accent },
    { x: -57, y: 15.06, z: 0, w: 3, h: 0.12, d: 22, color: t.accent },
    ...mirrorZ([
      { x: 1, y: 15.06, z: 12.7, w: 118, h: 0.12, d: 1.4, color: t.color },
    ]),
    // Island superstructure (starboard, inboard of the deck-edge guns):
    // stacked blocks, bridge glass, funnel, mast and radar.
    { x: 14, y: 17.5, z: 8.5, w: 22, h: 5, d: 6.5, color: superstructure },
    { x: 12, y: 21.5, z: 8.5, w: 14, h: 3, d: 5.5, color: superstructure },
    { x: 16, y: 22.0, z: 8.5, w: 4.5, h: 2.0, d: 5.8, color: glass },
    { x: 8, y: 24.0, z: 8.5, w: 5, h: 2.4, d: 4.5, color: funnelDark },
    { x: 8, y: 25.4, z: 8.5, w: 4.2, h: 0.8, d: 3.8, color: 0x16171a },
    { x: 18, y: 26.0, z: 8.5, w: 0.8, h: 6, d: 0.8, color: gunmetal },
    { x: 18, y: 28.6, z: 8.5, w: 5, h: 0.6, d: 1.2, color: gunmetal },
    { x: 14, y: 20.3, z: 8.5, w: 21, h: 0.6, d: 6.9, color: shade(t.color, 0.9) },
    // Nav lights at the bow.
    { x: 66, y: 10.5, z: 4.5, w: 0.8, h: 0.8, d: 0.8, color: navGreen },
    { x: 66, y: 10.5, z: -4.5, w: 0.8, h: 0.8, d: 0.8, color: navRed },
  ];
  return boxes;
}

// Destroyer escort: a slim ~70 m hull with a deckhouse, raked funnels
// wearing the team band, and small turrets fore and aft.
function destroyerBoxes(t) {
  return [
    // Hull with boot line, tapered bow and stern.
    { x: 0, y: 1.6, z: 0, w: 54, h: 5.2, d: 8.4, color: hullGray },
    { x: 0, y: -1.2, z: 0, w: 54, h: 3, d: 8.8, color: hullDark },
    { x: 30, y: 1.8, z: 0, w: 8, h: 4.8, d: 6, color: hullGray },
    { x: 35.5, y: 2.4, z: 0, w: 4, h: 3.6, d: 3.4, color: hullGray },
    { x: -30, y: 1.6, z: 0, w: 6, h: 4.4, d: 6.6, color: hullGray },
    // Weather deck.
    { x: 0, y: 4.5, z: 0, w: 56, h: 0.8, d: 8.0, color: deckDark },
    // Deckhouse with bridge glass and team stripe.
    { x: 8, y: 6.4, z: 0, w: 18, h: 3.2, d: 6.2, color: superstructure },
    { x: 13, y: 8.7, z: 0, w: 7, h: 2.2, d: 5.2, color: superstructure },
    { x: 16, y: 9.0, z: 0, w: 1.6, h: 1.6, d: 5.4, color: glass },
    { x: 8, y: 8.2, z: 0, w: 18.4, h: 0.5, d: 6.5, color: shade(t.color, 0.9) },
    // Funnels with the team band.
    { x: 2, y: 10.0, z: 0, w: 3.2, h: 4.4, d: 2.6, color: funnelDark },
    { x: -6, y: 9.4, z: 0, w: 3.2, h: 3.6, d: 2.6, color: funnelDark },
    { x: 2, y: 11.4, z: 0, w: 3.4, h: 0.9, d: 2.8, color: t.color },
    { x: -6, y: 10.6, z: 0, w: 3.4, h: 0.9, d: 2.8, color: t.color },
    // Mast.
    { x: 11, y: 12.2, z: 0, w: 0.6, h: 4.4, d: 0.6, color: gunmetal },
    // Turrets: blocky houses with stubby barrels, fore and aft.
    { x: 24, y: 5.9, z: 0, w: 5, h: 2.2, d: 4.4, color: shade(t.body, 0.8) },
    { x: 28.5, y: 6.1, z: 0, w: 4.5, h: 0.7, d: 0.7, color: gunmetal },
    { x: -20, y: 5.9, z: 0, w: 5, h: 2.2, d: 4.4, color: shade(t.body, 0.8) },
    { x: -24.5, y: 6.1, z: 0, w: 4.5, h: 0.7, d: 0.7, color: gunmetal },
    // Nav lights.
    { x: 37, y: 4.6, z: 1.6, w: 0.6, h: 0.6, d: 0.6, color: navGreen },
    { x: 37, y: 4.6, z: -1.6, w: 0.6, h: 0.6, d: 0.6, color: navRed },
  ];
}

// Battleship: a ~115 m armored hull whose weather deck sits at y=11,
// matching internal/game/ships.go (battleshipDeckY). The three main-battery
// turrets are NOT part of this mesh — they're live AAGun objects drawn by
// world.js — so the hull only carries their barbettes (at ship-local
// x 34 / 16 / -32) plus the superstructure between them.
function battleshipBoxes(t) {
  return [
    // Hull: low armored slab with a boot line, tapered bow and stern.
    { x: 0, y: 3.5, z: 0, w: 96, h: 13, d: 22, color: hullGray },
    { x: 0, y: -1.5, z: 0, w: 96, h: 5, d: 22.6, color: hullDark },
    { x: 54, y: 4.5, z: 0, w: 14, h: 11, d: 15, color: hullGray },
    { x: 63, y: 5.5, z: 0, w: 6, h: 9, d: 8, color: hullGray },
    { x: 67, y: 6.5, z: 0, w: 4, h: 6, d: 4.5, color: hullGray },
    { x: -52, y: 4.5, z: 0, w: 10, h: 11, d: 17, color: hullGray },
    // Armor belt band in the team color.
    { x: 0, y: 8.8, z: 0, w: 100, h: 1.6, d: 22.8, color: shade(t.body, 0.85) },
    // Weather deck.
    { x: 2, y: 10.5, z: 0, w: 116, h: 1.2, d: 21, color: deckDark },
    // Barbettes under the turret pivots (A fore, B superfiring, X aft).
    { x: 34, y: 12.2, z: 0, w: 9, h: 2.4, d: 9, color: hullGray },
    { x: 16, y: 13.6, z: 0, w: 9, h: 5.2, d: 9, color: hullGray },
    { x: -32, y: 12.2, z: 0, w: 9, h: 2.4, d: 9, color: hullGray },
    // Superstructure tower between B turret and the aft turret.
    { x: -3, y: 14.0, z: 0, w: 16, h: 6, d: 10, color: superstructure },
    { x: -3, y: 19.0, z: 0, w: 10, h: 4, d: 7, color: superstructure },
    { x: 1, y: 19.4, z: 0, w: 1.6, h: 1.8, d: 6.2, color: glass },
    { x: -4, y: 22.6, z: 0, w: 6, h: 3.2, d: 5, color: superstructure },
    { x: -3, y: 17.2, z: 0, w: 16.4, h: 0.6, d: 10.4, color: shade(t.color, 0.9) },
    // Rangefinder bar topside: the ballistic computer's eyes.
    { x: -4, y: 24.8, z: 0, w: 2, h: 1, d: 9, color: gunmetal },
    // Funnel with the team band, raked mast aft of it.
    { x: -15, y: 16.5, z: 0, w: 5, h: 7, d: 4.2, color: funnelDark },
    { x: -15, y: 20.3, z: 0, w: 5.2, h: 0.9, d: 4.4, color: t.color },
    { x: -22, y: 17.5, z: 0, w: 0.8, h: 9, d: 0.8, color: gunmetal },
    { x: -22, y: 21.4, z: 0, w: 4, h: 0.6, d: 1, color: gunmetal },
    // Nav lights at the bow.
    { x: 65, y: 9.8, z: 3.4, w: 0.7, h: 0.7, d: 0.7, color: navGreen },
    { x: 65, y: 9.8, z: -3.4, w: 0.7, h: 0.7, d: 0.7, color: navRed },
  ];
}

// Spectator vantage point per hull kind, in ship-local coordinates (bow
// +X, up +Y): eye sits atop the island / bridge / conning tower, look aims
// out over the bow, clear of masts and funnels.
const BRIDGE_VIEWS = [
  { eye: [4, 28, 8.5], look: [90, 10, 0] }, // carrier: island top
  { eye: [6, 13.5, 2], look: [70, 2, 0] }, // destroyer: above the bridge
  { eye: [-4, 27, 0], look: [80, 8, 0] }, // battleship: conning tower
];

const shipMaterial = new THREE.MeshLambertMaterial({ vertexColors: true });
const shipGeoCache = new Map(); // `${team}:${kind}` -> geometry

// The raw voxel box list for a hull, before merging (the break-up needs it).
function shipBoxes(team, kind) {
  const t = TEAM_INFO[team] ?? TEAM_INFO[0];
  return kind === SHIP_CARRIER ? carrierBoxes(t)
    : kind === SHIP_BATTLESHIP ? battleshipBoxes(t)
    : destroyerBoxes(t);
}

function shipGeometry(team, kind) {
  const key = `${team}:${kind}`;
  if (shipGeoCache.has(key)) return shipGeoCache.get(key);
  const geo = mergeBoxes(shipBoxes(team, kind));
  shipGeoCache.set(key, geo);
  return geo;
}

// Midships break plane (ship-local X) where a torpedoed hull jackknifes (#7).
const BREAK_X = { 0: -6, 1: 0, 2: 4 }; // carrier / destroyer / battleship

// Build one half of a hull, clipped at the break plane bx and re-centered so the
// break sits at the half's local origin (it becomes the hinge the half rotates
// about). Mirrors how planes.js cuts a torn wing at its shear line.
function shipHalfGeometry(team, kind, half, bx) {
  const out = [];
  for (const b of shipBoxes(team, kind)) {
    const x0 = b.x - b.w / 2, x1 = b.x + b.w / 2;
    if (half === "bow") {
      if (x1 <= bx) continue; // fully aft of the break
      const nx0 = Math.max(x0, bx);
      out.push({ ...b, x: (nx0 + x1) / 2 - bx, w: x1 - nx0 });
    } else {
      if (x0 >= bx) continue; // fully forward of the break
      const nx1 = Math.min(x1, bx);
      out.push({ ...b, x: (x0 + nx1) / 2 - bx, w: nx1 - x0 });
    }
  }
  return mergeBoxes(out);
}

/** Each team's anchored naval group, built from the welcome's ship list.
 * Hulls are torpedo-killable: snapshots carry hp/afloat, and a killed hull
 * settles below the waves with a slow roll until it refloats. */
export class Ships {
  constructor(scene, particles = null) {
    this.scene = scene;
    this.particles = particles; // for the break-up secondaries + steam (#7)
    this.ships = new Map(); // id -> {mesh, site, alive, sink, broken}
    this.sites = []; // kept for the minimap
  }

  build(sites) {
    for (const s of this.ships.values()) {
      this.scene.remove(s.mesh);
      this._disposeBroken(s);
    }
    this.ships.clear();
    this.sites = sites;
    for (const s of sites) {
      const mesh = new THREE.Mesh(shipGeometry(s.team, s.kind), shipMaterial);
      mesh.position.set(s.x, s.y, s.z);
      mesh.rotation.y = s.yaw;
      this.scene.add(mesh);
      this.ships.set(s.id, { mesh, site: s, alive: true, sink: 0, broken: null });
    }
  }

  /** Apply a snapshot's hull rows: [id, hp, alive]. */
  applySnapshot(rows) {
    for (const [id, hp, alive] of rows) {
      const sh = this.ships.get(id);
      if (!sh) continue;
      sh.hp = hp;
      sh.alive = alive === 1;
    }
  }

  /** A hull went down. A torpedo folds it in half (#7); everything else keeps
   *  the settle-and-list path, so the death tells you the weapon. */
  beginSink(id, cause) {
    const sh = this.ships.get(id);
    if (!sh || sh.broken || cause !== "torpedo") return;
    const s = sh.site;
    const bx = BREAK_X[s.kind] ?? 0;
    sh.mesh.visible = false;
    // A group at the break point in the ship's frame; the two halves are its
    // children, hinged at the origin so rotating them jackknifes the hull.
    const grp = new THREE.Group();
    const c = Math.cos(s.yaw), sn = Math.sin(s.yaw);
    grp.position.set(s.x + bx * c, s.y, s.z - bx * sn);
    grp.rotation.y = s.yaw;
    const bow = new THREE.Mesh(shipHalfGeometry(s.team, s.kind, "bow", bx), shipMaterial);
    const stern = new THREE.Mesh(shipHalfGeometry(s.team, s.kind, "stern", bx), shipMaterial);
    grp.add(bow, stern);
    this.scene.add(grp);
    sh.broken = { grp, bow, stern, t: 0, secAt: 0 };
  }

  _disposeBroken(sh) {
    if (!sh.broken) return;
    this.scene.remove(sh.broken.grp);
    sh.broken.bow.geometry.dispose();
    sh.broken.stern.geometry.dispose();
    sh.broken = null;
  }

  update(dt) {
    for (const sh of this.ships.values()) {
      // Refloated (server flipped afloat back on): swap the whole hull back in.
      if (sh.alive && (sh.broken || sh.sink > 0)) {
        this._disposeBroken(sh);
        sh.sink = 0;
        sh.mesh.visible = true;
      }
      if (sh.broken) {
        this._stepBreak(sh, dt);
        continue;
      }
      const target = sh.alive ? 0 : 1;
      if (sh.sink === target) continue;
      sh.sink += (sh.alive ? -dt : dt) / 8; // ~8 s down, same back up
      sh.sink = Math.min(1, Math.max(0, sh.sink));
      const s = sh.site;
      sh.mesh.position.y = s.y - sh.sink * 30;
      sh.mesh.rotation.z = sh.sink * 0.35; // settles with a list to starboard
      sh.mesh.rotation.y = s.yaw;
      sh.mesh.visible = sh.sink < 0.999;
    }
  }

  // The ~10 s jackknife: the bow rears up dripping, the stern folds down and
  // goes under first, the halves drift apart, and secondaries + steam ripple
  // over the break in the first few seconds.
  _stepBreak(sh, dt) {
    const b = sh.broken;
    b.t += dt;
    // Fold phase (first ~4 s): the hull jackknifes at the break — the bow rears
    // up dripping, the stern folds down — while both still ride near the surface.
    const fold = Math.min(1, b.t / 4);
    b.bow.rotation.z = fold * 0.95; // bow rears up
    b.stern.rotation.z = -fold * 0.6; // stern folds under
    b.bow.position.x = fold * 4; // the halves pull apart at the break
    b.stern.position.x = -fold * 4;
    // Sink phase: the stern leads it under, the bow slips down last.
    b.stern.position.y = -Math.pow(Math.max(0, (b.t - 1.5) / 8.5), 1.5) * 40;
    b.bow.position.y = -Math.pow(Math.max(0, (b.t - 5) / 5), 1.5) * 44;
    const k = b.t / 10;
    if (this.particles && b.t < 3 && b.t >= b.secAt) {
      b.secAt = b.t + 0.7;
      b.grp.updateMatrixWorld();
      const deck = b.grp.localToWorld(new THREE.Vector3((Math.random() - 0.5) * 20, 8, (Math.random() - 0.5) * 6));
      this.particles.explosion(deck);
      // A gout of white steam where the break meets the water.
      const brk = b.grp.localToWorld(new THREE.Vector3(0, 1, 0));
      this.particles.spawn(brk, {
        count: 14, speed: 7, life: 2.6, size: 3.4,
        colors: [0xeef2f6, 0xd8dde2, 0xc4cace, 0xb6bcc2],
        gravity: -6, drag: 0.9, spin: 0.4, grow: true,
      });
    }
    if (k >= 1) this.scene.remove(b.grp); // fully under; leave it removed
  }

  positionOf(id) {
    return this.ships.get(id)?.mesh.position ?? null;
  }

  /** Spectator bridge view: world-space eye/look for a hull's conning
   * tower or island bridge (rides the mesh, so a settling hull carries the
   * camera down with it). */
  bridgeOf(id) {
    const sh = this.ships.get(id);
    if (!sh) return null;
    const v = BRIDGE_VIEWS[sh.site.kind] ?? BRIDGE_VIEWS[1];
    sh.mesh.updateMatrixWorld();
    return {
      eye: sh.mesh.localToWorld(new THREE.Vector3(...v.eye)),
      look: sh.mesh.localToWorld(new THREE.Vector3(...v.look)),
    };
  }

  /** Minimap blips: live state rides on the welcome's static sites. */
  blips() {
    const out = [];
    for (const sh of this.ships.values()) {
      out.push({ x: sh.site.x, z: sh.site.z, team: sh.site.team, kind: sh.site.kind, alive: sh.alive });
    }
    return out;
  }
}
